import { useEffect, useState } from "react";

import { track } from "../utils/analytics";
import { useSubmitConsent } from "../utils/consent";
import Button from "./ui/Button";

/**
 * Marketing consent for a brand partner whose work is in this order.
 *
 * Everything shown here — the two statements, the intro, the withdrawal note,
 * the version string — is passed in rather than living in this file. The sentence
 * a collector agreed to is the thing we have to be able to produce later, so the
 * copy must travel with the record it is stored against and the two can never
 * drift. This component only decides how it looks.
 *
 * DEMO: that copy currently comes from utils/consent.js and the answer is saved
 * to localStorage, because the API has no consent endpoint yet. The prop shape
 * is the one the endpoint should return, so only the source needs swapping.
 *
 * Renders nothing unless the order actually carries a partner prompt, so it is
 * safe to mount on every order.
 */

function formatSavedAt(value) {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Splits a statement on its {privacy_policy} placeholder so the label can be a
 * real link. Clicking an anchor inside a <label> follows the link instead of
 * toggling the control — the HTML spec skips label activation when the click
 * lands on interactive content — so the checkbox is not toggled by mistake.
 */
function Statement({ text, placeholder, url, label }) {
  if (!placeholder || !url || !text.includes(placeholder)) {
    return text;
  }

  const [before, ...rest] = text.split(placeholder);

  return (
    <>
      {before}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline decoration-dotted underline-offset-4 hover:text-collectors-gray"
      >
        {label}
      </a>
      {rest.join(placeholder)}
    </>
  );
}

function Checkbox({ id, checked, onChange, disabled, children }) {
  return (
    <label className="flex gap-3 items-start cursor-pointer">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className="shrink-0 mt-0.5 w-[17px] h-[17px] flex items-center justify-center bg-white
          border border-solid border-collectors-black transition-colors
          peer-checked:bg-collectors-black peer-focus-visible:outline peer-focus-visible:outline-2
          peer-focus-visible:outline-offset-2 peer-focus-visible:outline-collectors-black"
      >
        {checked && (
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path
              d="M1 4.4L4 7.5L10 1.2"
              stroke="#ffffff"
              strokeWidth="1.8"
              strokeLinecap="square"
            />
          </svg>
        )}
      </span>
      <span className="font-sans text-sm leading-snug text-collectors-black">{children}</span>
    </label>
  );
}

export default function ConsentCard({ consent, orderId, zipCode }) {
  const current = consent?.current || null;

  const [marketing, setMarketing] = useState(Boolean(current?.marketing_consent));
  const [sharing, setSharing] = useState(Boolean(current?.partner_sharing_consent));
  const [saved, setSaved] = useState(current);
  const [editing, setEditing] = useState(!current);

  const { mutate: submitConsent, isPending, error } = useSubmitConsent();

  const partner = consent?.partner;
  const version = consent?.version;

  useEffect(() => {
    if (!partner) return;

    track("Partner Consent Shown", {
      order_id: orderId,
      partner,
      version,
      already_recorded: Boolean(current),
    });
    // Fires once per mount: this is an impression, not a re-render count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner]);

  if (!consent || !partner) {
    return null;
  }

  const handleSave = () => {
    submitConsent(
      {
        orderId,
        zipCode,
        partner,
        marketingConsent: marketing,
        partnerSharingConsent: sharing,
      },
      {
        onSuccess: (data) => {
          setSaved({
            marketing_consent: data.marketing_consent,
            partner_sharing_consent: data.partner_sharing_consent,
            captured_at: data.captured_at,
          });
          setEditing(false);
          track("Partner Consent Saved", {
            order_id: orderId,
            partner,
            version,
            marketing_consent: data.marketing_consent,
            partner_sharing_consent: data.partner_sharing_consent,
          });
        },
        onError: (err) =>
          track("Partner Consent Failed", {
            order_id: orderId,
            partner,
            version,
            error_message: err?.message ?? null,
          }),
      },
    );
  };

  const handleEdit = () => {
    setMarketing(Boolean(saved?.marketing_consent));
    setSharing(Boolean(saved?.partner_sharing_consent));
    setEditing(true);
    track("Partner Consent Reopened", { order_id: orderId, partner, version });
  };

  const savedAt = formatSavedAt(saved?.captured_at);
  const bothDeclined = saved && !saved.marketing_consent && !saved.partner_sharing_consent;

  return (
    <section className="bg-white border border-solid border-collectors-black p-5 md:p-6 mb-12 animate-slide-up">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {editing ? (
          <span className="bg-collectors-black text-white px-2 py-0.5 font-mono text-2xs uppercase tracking-widest">
            Optional
          </span>
        ) : (
          <span
            className={`px-2 py-0.5 font-mono text-2xs uppercase tracking-widest border border-solid ${
              bothDeclined
                ? "border-collectors-light text-collectors-gray"
                : "border-green-500 bg-green-500 text-white"
            }`}
          >
            Saved
          </span>
        )}
        <span className="px-2 py-0.5 font-mono text-2xs uppercase tracking-widest border border-solid border-collectors-light text-collectors-gray">
          Partner preferences
        </span>
      </div>

      <h3 className="font-display text-xl md:text-2xl font-bold uppercase tracking-tighter leading-tight mb-2">
        {consent.display_name}
      </h3>

      {editing ? (
        <>
          {consent.intro && (
            <p className="font-sans text-sm leading-relaxed text-collectors-black mb-5">
              {consent.intro}
            </p>
          )}

          <fieldset className="flex flex-col gap-4 border-t border-b border-solid border-collectors-light py-4 mb-4">
            <legend className="sr-only">{consent.display_name} marketing preferences</legend>

            <Checkbox
              id="consent-marketing"
              checked={marketing}
              onChange={setMarketing}
              disabled={isPending}
            >
              <Statement
                text={consent.marketing_statement}
                placeholder={consent.privacy_policy_placeholder}
                url={consent.privacy_policy_url}
                label={consent.privacy_policy_label}
              />
            </Checkbox>

            <Checkbox
              id="consent-sharing"
              checked={sharing}
              onChange={setSharing}
              disabled={isPending}
            >
              <Statement
                text={consent.sharing_statement}
                placeholder={consent.privacy_policy_placeholder}
                url={consent.privacy_policy_url}
                label={consent.privacy_policy_label}
              />
            </Checkbox>
          </fieldset>

          {consent.withdrawal_note && (
            <p className="font-sans text-xs leading-relaxed text-collectors-gray mb-5">
              {consent.withdrawal_note}
            </p>
          )}

          {error && (
            <p className="font-mono text-2xs text-red-600 uppercase tracking-widest mb-4">
              {error.message}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4">
            <Button type="button" onClick={handleSave} disabled={isPending}>
              {isPending ? "Saving..." : "Save preferences"}
            </Button>
            {consent.bound_to && (
              <p className="font-mono text-2xs uppercase tracking-wide text-collectors-gray leading-relaxed max-w-[34ch]">
                Saved against {consent.bound_to}, the email on order No. {orderId}
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="font-mono text-xs uppercase tracking-wide leading-loose mb-4">
            <p className={saved?.marketing_consent ? "" : "text-collectors-gray"}>
              {saved?.marketing_consent ? "✓" : "×"} Marketing emails &mdash;{" "}
              {saved?.marketing_consent ? "yes" : "no"}
            </p>
            <p className={saved?.partner_sharing_consent ? "" : "text-collectors-gray"}>
              {saved?.partner_sharing_consent ? "✓" : "×"} Share with Official Partners &mdash;{" "}
              {saved?.partner_sharing_consent ? "yes" : "no"}
            </p>
            {savedAt && <p className="text-collectors-gray">{savedAt}</p>}
          </div>

          <button
            type="button"
            onClick={handleEdit}
            className="font-mono text-xs uppercase tracking-wide text-collectors-gray underline
              decoration-dotted underline-offset-4 hover:text-collectors-black transition-colors"
          >
            [ Update preferences ]
          </button>
        </>
      )}

      {version && (
        <p className="font-mono text-2xs uppercase tracking-wide text-collectors-gray border-t border-solid border-collectors-light mt-5 pt-3">
          Ref: {version}
        </p>
      )}
    </section>
  );
}
